import { useEffect, useMemo, useRef, useCallback, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { PublicProducerHeader } from "@/components/producer/PublicProducerHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useProducer } from "@/hooks/useProducer";
import { useShare } from "@/hooks/useShare";
import { ArrowLeft } from "lucide-react";
import { Seo } from "@/components/common";
import { createLocalBusinessJsonLd } from "@/lib/seo";
import { PreviewBanner } from "@/components/producer/PreviewBanner";
import {
  ProducerHero,
  ProducerAbout,
  ProducerEvents,
  ProducerVisit,
  ProducerContact,
  ProducerGallery,
  ProducerAccommodations,
} from "@/components/producer";
import { ProducerFoodFika } from "@/components/producer/ProducerFoodFika";
import { ProducerSignalsRow } from "@/components/signals/ProducerSignalsRow";
import { ProducerProducts } from "@/components/producer/ProducerProducts";
import { ProducerFleaMarkets } from "@/components/producer/ProducerFleaMarkets";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getEffectiveProducerLocation, buildGoogleMapsUrl } from "@/lib/producer/getEffectiveLocation";
import { hasPublicFarmShopAccess } from '@/lib/shop/publicFarmShopAccess';
import { useLocale, pickLocalized } from "@/i18n";
import { useEntityTranslations } from "@/hooks/useEntityTranslations";

const ProducerDetail = () => {
  const [searchParams] = useSearchParams();
  const { slugOrId } = useParams<{ slugOrId: string }>();
  const { producer, enabledFeatures, loading, error, isPreview } = useProducer(slugOrId || "");
  const { share } = useShare();

  // Fetch linked place for effective location resolution
  const { data: linkedPlace } = useQuery({
    queryKey: ['producer-linked-place', producer?.place_id],
    enabled: !!producer?.place_id,
    queryFn: async () => {
      const { data } = await supabase
        .from('places')
        .select('slug, name, address_text, municipality, landskap, lat, lng')
        .eq('id', producer!.place_id!)
        .maybeSingle();
      return data;
    },
    staleTime: 5 * 60_000,
  });

  // Effective location
  const effectiveLocation = useMemo(
    () => getEffectiveProducerLocation(producer, linkedPlace),
    [producer, linkedPlace],
  );

  // Content translations (description, short_description, food_description)
  const { locale } = useLocale();
  const { data: translations } = useEntityTranslations("producer", producer?.id ?? null, locale);

  const localizedDescription = useMemo(
    () => pickLocalized(producer?.description ?? "", translations, "description", locale).value,
    [producer?.description, translations, locale],
  );
  const localizedShortDescription = useMemo(
    () => pickLocalized(producer?.short_description ?? "", translations, "short_description", locale).value,
    [producer?.short_description, translations, locale],
  );
  const localizedFoodDescription = useMemo(
    () => pickLocalized((producer as any)?.food_description ?? "", translations, "food_description", locale).value,
    [(producer as any)?.food_description, translations, locale],
  );

  // Section refs for smooth scrolling
  const visitRef = useRef<HTMLElement>(null);
  const heroRef = useRef<HTMLElement>(null);

  // Compact header state via IntersectionObserver
  const [isCompact, setIsCompact] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsCompact(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-48px 0px 0px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [producer]);

  const scrollToMap = useCallback(() => {
    visitRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Update document title
  useEffect(() => {
    if (producer) {
      document.title = `${producer.name} – Farmly`;
    }
  }, [producer]);

  const handleShare = () => {
    if (!producer) return;
    share({
      kind: "producer",
      name: producer.name,
      slug: producer.slug || producer.id,
      placeName: producer.city || null,
      landskap: producer.region || producer.county || null,
    });
  };

  const googleMapsUrl = useMemo(
    () => producer ? buildGoogleMapsUrl(effectiveLocation, producer.name) : '#',
    [effectiveLocation, producer],
  );

  const linkedPlaceHref = linkedPlace?.slug ? `/place/${linkedPlace.slug}` : null;
  const linkedPlaceName = linkedPlace?.name || producer?.city || null;
  const producerBackTo = linkedPlaceHref || '/explore';
  const producerBackLabel = linkedPlaceName ? `Till ${linkedPlaceName}` : 'Till Utforska';
  
  

  const isNotPublished = producer?.status !== 'public';
  const isPreviewMode = searchParams.get("preview") === "1" || searchParams.get("preview") === "true";
  const portalRoute = `/app/producer/${producer?.id || ''}/grundinfo`;
  
  // Build canonical URL
  const canonicalUrl = useMemo(() => {
    if (!producer) return '';
    const baseUrl = window.location.origin;
    const path = producer.slug ? `/producer/${producer.slug}` : `/producer/${producer.id}`;
    return `${baseUrl}${path}`;
  }, [producer]);
  
  // Build JSON-LD for LocalBusiness (enhanced with farm sale amenity)
  const jsonLd = useMemo(() => {
    if (!producer || isPreview || isNotPublished) return undefined;
    
    const ld = createLocalBusinessJsonLd({
      name: producer.name,
      description: producer.short_description || producer.description || undefined,
      url: canonicalUrl,
      image: undefined,
      address: {
        streetAddress: effectiveLocation.addressText || undefined,
        addressLocality: effectiveLocation.city || undefined,
        addressRegion: effectiveLocation.region || undefined,
        addressCountry: "SE",
      },
      telephone: producer.phone || undefined,
      email: producer.email || undefined,
      geo: effectiveLocation.lat && effectiveLocation.lng ? {
        latitude: effectiveLocation.lat,
        longitude: effectiveLocation.lng,
      } : undefined,
    });

    // Enhance with farm sale amenity feature
    if (producer.has_farm_sale_alcohol) {
      (ld as any).amenityFeature = {
        "@type": "LocationFeatureSpecification",
        name: "Gårdsförsäljning av alkohol",
        value: true,
      };
    }
    
    return ld;
  }, [producer, canonicalUrl, isPreview, isNotPublished, effectiveLocation]);
  
  // Build description for SEO — use localized short_description when available
  const seoDescription = useMemo(() => {
    if (!producer) return '';
    const desc = localizedShortDescription || producer.short_description || localizedDescription || producer.description || "";
    return desc.slice(0, 160);
  }, [producer, localizedShortDescription, localizedDescription]);

  // Determine if map section exists
  const hasMapSection = producer && (producer.lat && producer.lng || producer.address_text || producer.opening_hours_text);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader density="compact" />
        <main className="flex-1 container py-8">
          <Skeleton className="h-5 w-24 mb-6" />
          <Skeleton className="h-16 w-3/4 mb-4" />
          <Skeleton className="h-8 w-1/2 mb-4" />
          <Skeleton className="h-5 w-1/4 mb-10" />
          <div className="flex gap-3 mb-16">
            <Skeleton className="h-12 w-36 rounded-full" />
            <Skeleton className="h-12 w-28 rounded-full" />
          </div>
          <Skeleton className="h-32 w-full max-w-2xl mb-8" />
          <Skeleton className="h-48 w-full max-w-3xl" />
        </main>
        <SiteFooter />
      </div>
    );
  }

  // 404 state
  if (!producer) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Helmet>
          <title>Sidan hittades inte – Farmly</title>
        </Helmet>
        <SiteHeader density="compact" />
        <main className="flex-1 container py-16 text-center">
          <h1 className="font-display text-3xl mb-4 text-foreground">
            Vi kunde inte hitta gården
          </h1>
          <p className="text-muted-foreground mb-8">
            {error || "Gården du letade efter verkar inte finnas."}
          </p>
          <Button asChild>
            <Link to="/explore">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till Utforska
            </Link>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col bg-background overflow-x-hidden">
      <Seo
        title={producer.name}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
        ogType="place"
        image={undefined}
        imageAlt={`${producer.name} – omslagsbild`}
        noIndex={isPreview || isNotPublished}
        jsonLd={jsonLd}
      />
      
      <PublicProducerHeader
        producerId={producer.id}
        producerSlug={producer.slug}
        producerName={producer.name}
        placeName={linkedPlaceName}
        backTo={producerBackTo}
        backLabel={producerBackLabel}
        isCompact={isCompact}
        onShare={handleShare}
      />

      {/* Preview banner — always shown in preview mode */}
      {isPreviewMode && (
        <PreviewBanner portalRoute={portalRoute} />
      )}

      <main className="flex-1">
        {/* 1. Hero */}
        <ProducerHero
          ref={heroRef}
          producer={producer}
          onShare={handleShare}
          googleMapsUrl={googleMapsUrl}
          placeName={linkedPlace?.name || producer.city || null}
          placeSlug={linkedPlace?.slug || (producer as any).place_slug || null}
          isPreviewMode={isPreviewMode}
          activeIndex={isMobile ? heroIndex : undefined}
          onHeroIndexChange={setHeroIndex}
        />

        {/* Content sections — two-column on desktop, single on mobile */}
        <div className="container">
          <div className="max-w-5xl pt-6 pb-12 md:grid md:grid-cols-[1fr_300px] md:gap-10">
            {/* Main column — editorial/story content */}
            <div className="space-y-8">
              {/* 1b. Primary signals — hero-near */}
              <ProducerSignalsRow
                hasFarmSaleAlcohol={producer.has_farm_sale_alcohol ?? false}
                hasFarmShop={producer.has_farm_shop ?? false}
                producerId={producer.id}
              />

              {/* 2. Visuellt andrum (gallery thumbnail strip) */}
              <ProducerGallery
                producerId={producer.id}
                producerName={producer.name}
                maxItems={3}
                activeHeroIndex={isMobile ? heroIndex : undefined}
                onSelectIndex={isMobile ? setHeroIndex : undefined}
              />

              {/* 3. Om gården (about) */}
              {(producer.description || producer.short_description) && (
                <ProducerAbout
                  description={localizedDescription || producer.description}
                  shortDescription={localizedShortDescription || producer.short_description || null}
                />
              )}

              {/* 3.5. Mat & fika (hideIfEmpty) */}
              <ProducerFoodFika
                foodDescription={localizedFoodDescription || (producer as any).food_description}
                foodServingTypes={(producer as any).food_serving_types}
              />

              {/* 4. Det händer här (events, hideIfEmpty) */}
              <ProducerEvents
                producerId={producer.id}
                producerSlug={producer.slug || undefined}
                producerName={producer.name}
                producerCity={producer.city || undefined}
                maxItems={3}
              />

              <ProducerFleaMarkets producerId={producer.id} />

              {/* 4.7. Boende (hideIfEmpty) */}
              <ProducerAccommodations producerId={producer.id} />

              {/* 4.5. Gårdsbutik (hideIfEmpty, gated via ShopGate) */}
              <ProducerProducts
                producerId={producer.id}
                producerName={producer.name}
                producerSlug={producer.slug}
                producerStatus={producer.status}
                producerBusinessStatus={producer.producer_status}
                producerVerified={producer.verified}
                producerShopEnabled={(producer as any).shop_enabled ?? false}
                hasFarmShopEntitlement={hasPublicFarmShopAccess({
                  enabledFeatures,
                  producerHasFarmShop: producer.has_farm_shop ?? false,
                })}
                pickupInstructions={(producer as any).pickup_instructions ?? null}
              />

              {/* Mobile only: Visit + Contact inline (hidden on desktop) */}
              <div className="md:hidden space-y-8">
                <ProducerVisit
                  ref={visitRef}
                  producer={producer}
                  googleMapsUrl={googleMapsUrl}
                  linkedPlace={linkedPlace}
                />
                <ProducerContact producer={producer} />
              </div>
            </div>

            {/* Sidebar — desktop only, compact utility blocks */}
            <aside className="hidden md:block">
              <div className="sticky top-20 space-y-6">
                <ProducerVisit
                  producer={producer}
                  googleMapsUrl={googleMapsUrl}
                  linkedPlace={linkedPlace}
                />
                <ProducerContact producer={producer} />
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default ProducerDetail;
